import { conversationSpaceId } from './conversation-space';
import { headerPresence, siteEditAction, type WebsitePresence } from './presence-checklist';

// "Edit in Symphony" from Wixel's site card (symphony-bff `GetSiteBuild.editInSymphonyUrl`) lands on the build's chat
// with the site it built: `?conversation=<build chat>&chat=fullscreen&siteEdit=<metasite id>`. Without it the owner
// got the chat alone and never saw which site they were editing (owner's report, 2026-11-01). With it the arrival
// does what the Space's own Edit does for a Symphony site: the chat is BOUND to the site (its "Preview site" link)
// and, where the chat can host it, the site's preview pane opens beside it (`lib/site-edit-store.ts`).
//
// Only the owner's OWN site opens: the id names a Wix site, and the preview address comes from the owner's Space that
// holds it, never from the link. An id no Space holds, or a site Symphony does not edit in chat, opens the chat alone.

/** The arrival's param: the metasite id of the site the chat is about. Stripped once read. */
export const SITE_EDIT_PARAM = 'siteEdit';

export interface SiteEditArrival {
  conversationId: string;
  metaSiteId: string;
}

/** The site-edit arrival in `search`, or null: both the conversation and the site must be named, the site as a GUID. */
export function siteEditArrival(search: string): SiteEditArrival | null {
  const params = new URLSearchParams(search);
  const conversationId = (params.get('conversation') ?? '').trim();
  const metaSiteId = conversationSpaceId(params.get(SITE_EDIT_PARAM))?.toLowerCase();
  return conversationId && metaSiteId ? { conversationId, metaSiteId } : null;
}

/**
 * The Space and preview address the arrival opens beside its chat, or null: the owner's Space whose website is that
 * site, when its Edit is Symphony's chat with a live address (`siteEditAction` CHAT, the same rule as the Space's Edit).
 */
export function siteEditTarget(
  workspaces: readonly { id?: string; website?: { metaSiteId?: string; presence?: WebsitePresence } | null; presence?: WebsitePresence }[],
  metaSiteId: string,
): { workspaceId: string; previewUrl: string } | null {
  const space = workspaces.find((w) => w.id && (w.website?.metaSiteId ?? '').trim().toLowerCase() === metaSiteId);
  const presence = headerPresence(space);
  const action = presence ? siteEditAction(presence, 'computer') : null;
  if (!space?.id || action?.kind !== 'chat' || !action.previewUrl) return null;
  return { workspaceId: space.id, previewUrl: action.previewUrl };
}
